/**
 * FelixVideo — testimonial video player (used by the Carousel – Reviews block).
 *
 * Pointer users get a controls-free player: the playhead toggles play/pause and
 * fades out while playing (returning on hover). A progress ring is drawn once on
 * connect, toggled by `is-playing`, and advanced on the throttled `timeupdate`
 * event rather than a per-frame loop. Keyboard/AT users (detected via Tab or
 * keyboard activation) get the browser's native controls so the media stays
 * operable (seek, volume, captions). The host gains `is-playing` and its parent
 * gains `is-video-playing` so overlays can clear. One plays at a time.
 *
 * Visual styling (the injected `.felix-video__*` hooks) is owned by the
 * consuming block's style.css.
 */
const RADIUS = 8
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

const PLAY_ICON =
	'<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 256 256" aria-hidden="true"><path fill="currentColor" d="M240 128a15.74 15.74 0 0 1-7.6 13.51L88.32 229.65a16 16 0 0 1-16.2.3A15.86 15.86 0 0 1 64 216.13V39.87a15.86 15.86 0 0 1 8.12-13.82 16 16 0 0 1 16.2.3l144.08 88.14A15.74 15.74 0 0 1 240 128Z"/></svg>'

const PAUSE_ICON =
	'<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 256 256" aria-hidden="true"><path fill="currentColor" d="M216 48v160a16 16 0 0 1-16 16h-40a16 16 0 0 1-16-16V48a16 16 0 0 1 16-16h40a16 16 0 0 1 16 16ZM96 32H56a16 16 0 0 0-16 16v160a16 16 0 0 0 16 16h40a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16Z"/></svg>'

// Keyboard-navigation detection: pointer users keep the controls-free player;
// keyboard/AT users get native controls. Tab marks keyboard nav; a pointer
// press clears it. Registered once when the component script loads.
let keyboardNav = false
document.addEventListener( 'keydown', ( e ) => { if ( e.key === 'Tab' ) keyboardNav = true }, true )
document.addEventListener( 'pointerdown', () => { keyboardNav = false }, true )

class FelixVideo extends HTMLElement {
	connectedCallback() {
		if ( this._ready ) return
		this._ready = true

		const btn = document.createElement( 'button' )
		btn.className = 'felix-video__play-btn'
		btn.type = 'button'
		btn.setAttribute( 'aria-label', 'Play video' )
		btn.innerHTML =
			'<span class="felix-video__play-icon">' + PLAY_ICON + '</span>' +
			'<span class="felix-video__pause-icon">' + PAUSE_ICON + '</span>'
		this.appendChild( btn )
		this._btn = btn

		// Progress ring — drawn once here, then only revealed/advanced (never rebuilt).
		const svg = document.createElementNS( 'http://www.w3.org/2000/svg', 'svg' )
		svg.setAttribute( 'viewBox', '0 0 20 20' )
		svg.setAttribute( 'aria-hidden', 'true' )
		svg.classList.add( 'felix-video__progress' )
		svg.innerHTML =
			'<circle class="felix-video__progress-track" cx="10" cy="10" r="' + RADIUS + '"/>' +
			'<circle class="felix-video__progress-bar" cx="10" cy="10" r="' + RADIUS +
			'" stroke-dasharray="' + CIRCUMFERENCE + '" stroke-dashoffset="' + CIRCUMFERENCE + '"/>'
		this.appendChild( svg )
		this._bar = svg.querySelector( '.felix-video__progress-bar' )

		// Whole tile toggles play/pause; the button's click bubbles here too (keyboard a11y).
		this.addEventListener( 'click', ( e ) => this.#toggle( e ) )
		// Clear the post-click suppression once the cursor actually leaves the tile.
		this.addEventListener( 'mouseleave', () => this.classList.remove( 'is-fresh-play' ) )
		// A keyboard user reaching the tile gets native controls.
		this.addEventListener( 'focusin', () => { if ( keyboardNav ) this.#enableNativeControls() } )
	}

	#ensureVideo() {
		if ( this._video ) return this._video

		const src = this.dataset.src
		if ( ! src ) return null

		const video = document.createElement( 'video' )
		video.src = src
		video.controls = false
		video.playsInline = true
		video.preload = 'metadata'
		video.className = 'felix-video__media absolute inset-0 w-full h-full object-cover block'

		const poster = this.querySelector( 'img' )
		if ( poster && poster.src ) video.poster = poster.src

		this.insertBefore( video, this._btn )
		this._video = video

		video.addEventListener( 'play', () => this.#onPlay() )
		video.addEventListener( 'pause', () => this.#onPause() )
		video.addEventListener( 'ended', () => this.#onEnded() )
		video.addEventListener( 'timeupdate', () => this.#advance() )

		return video
	}

	#toggle( e ) {
		// Once native controls are showing, let them own pointer interactions.
		if ( this._video && this._video.controls && e && e.detail !== 0 ) return

		const keyboard = keyboardNav || ( e && e.detail === 0 )
		const video = this.#ensureVideo()
		if ( ! video ) return
		if ( keyboard ) this.#enableNativeControls()
		if ( video.paused ) {
			// Swallow the AbortError thrown when this promise is interrupted by a
			// near-immediate pause (e.g. another tile claiming playback).
			video.play().catch( () => {} )
		} else {
			video.pause()
		}
	}

	// Expose the browser's native control bar (seek/volume/captions) for
	// keyboard/AT users; the custom progress ring steps aside via CSS.
	#enableNativeControls() {
		this.classList.add( 'is-native-controls' )
		if ( this._video ) this._video.controls = true
	}

	#onPlay() {
		document.querySelectorAll( 'felix-video.is-playing' ).forEach( ( other ) => {
			if ( other !== this && other._video && ! other._video.paused ) other._video.pause()
		} )
		// `is-fresh-play` suppresses the hover-reveal until the cursor leaves and returns.
		this.classList.add( 'is-playing', 'is-fresh-play' )
		// Class fallback for engines without :has() — drives the overlay-clear too.
		if ( this.parentElement ) this.parentElement.classList.add( 'is-video-playing' )
		this._btn.setAttribute( 'aria-label', 'Pause video' )
	}

	#onPause() {
		this.classList.remove( 'is-playing' )
		if ( this.parentElement ) this.parentElement.classList.remove( 'is-video-playing' )
		this._btn.setAttribute( 'aria-label', 'Play video' )
	}

	#onEnded() {
		this.#onPause()
		// Snap the ring back to empty without tweening (the bar carries a
		// stroke-dashoffset transition, which would otherwise sweep it backward).
		if ( this._bar ) {
			this._bar.style.transition = 'none'
			this._bar.style.strokeDashoffset = CIRCUMFERENCE
			void this._bar.getBoundingClientRect()
			this._bar.style.transition = ''
		}
	}

	#advance() {
		const video = this._video
		if ( video && this._bar && Number.isFinite( video.duration ) && video.duration > 0 ) {
			this._bar.style.strokeDashoffset = CIRCUMFERENCE * ( 1 - video.currentTime / video.duration )
		}
	}
}

customElements.define( 'felix-video', FelixVideo )
